import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { buildSearchParams } from '../_helpers/utils';
import { APIResponse } from '../_types/api-responses.types';
import {
  FlowInvoicesFilters,
  ListFlowInvoicesResponse,
} from '../_types/billing.types';
import {
  AllInsuranceFlowsResponse,
  CancelInsuranceFlowContractRequest,
  CancelInsuranceFlowContractResponse,
  CentralisationFlowRequest,
  CentralisationFlowResponse,
  GetInsuranceFlowsParams,
  InsuranceFlowDetailsResponse,
  InsuranceFlowFullHistoryResponse,
  InsuranceFlowsExportParams,
  InsuranceFlowTasksResponse,
  RenderCentralisationMailsResponse,
  RenderCompanyMailResponse,
  SearchInsuranceFlowsParams,
  SearchInsuranceFlowsResponse,
  SendCentralisationFlowRequest,
  SendInsuranceFlowContractRequest,
  SendInsuranceFlowContractResponse,
} from '../_types/insurance-flow.types';
import { AllTasksRequest } from '../_types/proposals.type';
import { UserAuthService } from './user-auth.service';

@Injectable({
  providedIn: 'root',
})
export class InsuranceFlowService {
  baseUrl: string;

  baseHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  constructor(
    private readonly httpClient: HttpClient,
    private readonly userAuthService: UserAuthService
  ) {
    this.baseUrl = `${environment.INSURANCEAPIURL}insurance-flow`;
  }

  get headers() {
    return this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
  }

  getInsuranceFlowDetails(
    id: string
  ): Observable<APIResponse<InsuranceFlowDetailsResponse>> {
    return this.httpClient.get<APIResponse<InsuranceFlowDetailsResponse>>(
      `${this.baseUrl}/details/${id}`,
      {
        headers: this.headers,
      }
    );
  }

  getInsuranceFlows(
    params: GetInsuranceFlowsParams
  ): Observable<APIResponse<AllInsuranceFlowsResponse>> {
    return this.httpClient.post<APIResponse<AllInsuranceFlowsResponse>>(
      this.baseUrl,
      params,
      {
        headers: this.headers,
      }
    );
  }

  /**
   * Type-ahead for the "Affaire" picker: one member's flows, capped and
   * searchable server-side.
   *
   * Much lighter than `getInsuranceFlows`, which returns full flow summaries
   * and has to be asked for an absurd page size to stand in for a search.
   */
  searchInsuranceFlows(
    params: SearchInsuranceFlowsParams
  ): Observable<APIResponse<SearchInsuranceFlowsResponse>> {
    const searchParams = buildSearchParams({ ...params });

    return this.httpClient.get<APIResponse<SearchInsuranceFlowsResponse>>(
      `${this.baseUrl}/search?${searchParams.toString()}`,
      { headers: this.headers }
    );
  }

  getAllTasks(
    params: AllTasksRequest
  ): Observable<APIResponse<InsuranceFlowTasksResponse>> {
    return this.httpClient.post<APIResponse<InsuranceFlowTasksResponse>>(
      `${this.baseUrl}/get/tasks`,
      params,
      {
        headers: this.headers,
      }
    );
  }

  exportInsuranceFlowsCsv(
    params: InsuranceFlowsExportParams
  ): Observable<string> {
    return this.httpClient.post(`${this.baseUrl}/export`, params, {
      headers: this.headers,
      responseType: 'text',
    });
  }

  getFlowFullHistory(
    id: string
  ): Observable<APIResponse<InsuranceFlowFullHistoryResponse>> {
    return this.httpClient.get<APIResponse<InsuranceFlowFullHistoryResponse>>(
      `${this.baseUrl}/${id}/full-history`,
      {
        headers: this.headers,
      }
    );
  }

  /**
   * Invoices of every proposal of every flow sharing this flow's display id.
   * Unpaginated and unsorted — the caller sorts client-side.
   */
  getFlowInvoices(
    flowId: string,
    filters: FlowInvoicesFilters = {}
  ): Observable<APIResponse<ListFlowInvoicesResponse>> {
    const params = buildSearchParams({ ...filters });
    return this.httpClient.get<APIResponse<ListFlowInvoicesResponse>>(
      `${this.baseUrl}/${flowId}/invoices?${params}`,
      { headers: this.headers }
    );
  }

  sendCentralisationFlow(
    body: SendCentralisationFlowRequest
  ): Observable<APIResponse<CentralisationFlowResponse>> {
    return this.httpClient.post<APIResponse<CentralisationFlowResponse>>(
      `${this.baseUrl}/centralisation/send`,
      body,
      { headers: this.headers }
    );
  }

  cancelCentralisationFlow(
    body: CentralisationFlowRequest
  ): Observable<APIResponse<CentralisationFlowResponse>> {
    return this.httpClient.post<APIResponse<CentralisationFlowResponse>>(
      `${this.baseUrl}/centralisation/cancel`,
      body,
      { headers: this.headers }
    );
  }

  renderCentralisation(
    flowId: string
  ): Observable<APIResponse<RenderCentralisationMailsResponse>> {
    return this.httpClient.get<APIResponse<RenderCentralisationMailsResponse>>(
      `${this.baseUrl}/centralisation/render/${flowId}`,
      { headers: this.headers }
    );
  }

  renderContract(
    proposalId: string
  ): Observable<APIResponse<RenderCompanyMailResponse>> {
    return this.httpClient.get<APIResponse<RenderCompanyMailResponse>>(
      `${this.baseUrl}/contract/render/${proposalId}`,
      { headers: this.headers }
    );
  }

  sendContract(
    body: SendInsuranceFlowContractRequest
  ): Observable<APIResponse<SendInsuranceFlowContractResponse>> {
    return this.httpClient.post<APIResponse<SendInsuranceFlowContractResponse>>(
      `${this.baseUrl}/contract/send`,
      body,
      { headers: this.headers }
    );
  }

  cancelContract(
    body: CancelInsuranceFlowContractRequest
  ): Observable<APIResponse<CancelInsuranceFlowContractResponse>> {
    return this.httpClient.post<
      APIResponse<CancelInsuranceFlowContractResponse>
    >(`${this.baseUrl}/contract/cancel`, body, { headers: this.headers });
  }
}
